import { TaskTypeEnum } from '@sr-sdks/api-sdk-axios';
import { getMsr2Base } from './embedAuth';
import { normalizeMessageValue } from './helpers/common';

export const EMBED_WORKFLOW_SRC_PARAM = 'src';
export const EMBED_WORKFLOW_TITLE_PARAM = 'workflowTitle';

const EMBED_PATH_BY_TYPE = {
  'add-title': 'embed/add-title',
  'competing-claim': 'embed/competing-claim',
  'consolidated-payments': 'embed/consolidated-payments',
  payments: 'embed/payments',
  'potential-claim': 'embed/potential-claim',
  title: 'embed/title',
} as const;

type EmbedType = keyof typeof EMBED_PATH_BY_TYPE;

/** A plain id: a decoded `%2F` in one could walk the path to another embed. */
const TRUSTED_RESOURCE_ID = '[a-zA-Z0-9-]+';

const TRUSTED_EMBED_PATH_PATTERN = new RegExp(
  `^embed/(${Object.keys(EMBED_PATH_BY_TYPE).join('|')})/(${TRUSTED_RESOURCE_ID})$`,
);

const TRUSTED_RESOURCE_ID_PATTERN = new RegExp(`^${TRUSTED_RESOURCE_ID}$`);

export const isTrustedResourceId = (
  resourceId?: string,
): resourceId is string =>
  Boolean(resourceId && TRUSTED_RESOURCE_ID_PATTERN.test(resourceId));

/** The one parse of `?src=`, so the iframe and the membership gate cannot disagree. */
const parseTrustedEmbedSrc = (embedSrc?: null | string) => {
  const match = embedSrc?.replace(/^\/+/, '').match(TRUSTED_EMBED_PATH_PATTERN);

  // The pattern is built from the map's keys, so the match is always one.
  return match
    ? { embedType: match[1] as EmbedType, resourceId: match[2] }
    : undefined;
};

// These are the possible values for the completed scenario
const COMPLETED_MESSAGE_VALUES = new Set([
  'complete',
  'completed',
  'registered',
  'success',
]);

// These are the possible values for the draft scenario
const DRAFT_MESSAGE_VALUES = new Set(['cancelled', 'draft']);

const CLOSE_MESSAGE_VALUES = new Set(['close', 'closed']);

export type EmbeddedWorkflowOutcome = 'closed' | 'completed' | 'draft';

export type EmbeddedWorkflowMessageData = {
  claimStatus?: string;
  completed?: boolean;
  isDraft?: boolean;
  leadStatus?: string;
  outcome?: string;
  status?: string;
  type?: string;
};

type NormalizedEmbeddedWorkflowMessage = Pick<
  EmbeddedWorkflowMessageData,
  'claimStatus' | 'leadStatus' | 'outcome' | 'status' | 'type'
>;

export const buildEmbedWorkflowUrl = (
  embedPath?: string,
  currentSearch = '',
  titleName?: string,
): string | undefined => {
  if (!embedPath) return undefined;

  const searchParams = new URLSearchParams(currentSearch);

  searchParams.set(EMBED_WORKFLOW_SRC_PARAM, embedPath);

  if (titleName?.trim()) {
    searchParams.set(EMBED_WORKFLOW_TITLE_PARAM, titleName.trim());
  } else {
    searchParams.delete(EMBED_WORKFLOW_TITLE_PARAM);
  }

  return `?${searchParams.toString()}`;
};

export const buildTaskEmbedPath = (
  taskType: TaskTypeEnum,
  resourceId?: string,
): string | undefined => {
  if (!resourceId) return undefined;

  switch (taskType) {
    case TaskTypeEnum.PotentialClaim:
      return `embed/potential-claim/${resourceId}`;
    case TaskTypeEnum.CompetingClaim:
      return `embed/competing-claim/${resourceId}`;
    case TaskTypeEnum.DraftRegistration:
      return `embed/title/${resourceId}`;
    default:
      return undefined;
  }
};

export const buildTaskEmbedWorkflowUrl = (
  taskType: TaskTypeEnum,
  resourceId?: string,
  currentSearch?: string,
  titleName?: string,
): string | undefined =>
  buildEmbedWorkflowUrl(
    buildTaskEmbedPath(taskType, resourceId),
    currentSearch,
    titleName,
  );

export const buildAddTitleEmbedWorkflowUrl = (
  workspaceId?: string,
  currentSearch?: string,
): string | undefined =>
  buildEmbedWorkflowUrl(
    workspaceId ? `embed/add-title/${workspaceId}` : undefined,
    currentSearch,
  );

/**
 * Drops the embed params so the host page falls back to its own content. Used
 * to close the workflow in place, without adding a history entry that Back
 * could return to.
 */
export const withoutEmbedWorkflowParams = (
  currentParams: URLSearchParams,
): URLSearchParams => {
  const nextParams = new URLSearchParams(currentParams);

  nextParams.delete(EMBED_WORKFLOW_SRC_PARAM);
  nextParams.delete(EMBED_WORKFLOW_TITLE_PARAM);

  return nextParams;
};

export const getTrustedIframeUrl = (
  embedSrc: null | string,
): string | undefined => {
  const trustedEmbed = parseTrustedEmbedSrc(embedSrc);

  if (!trustedEmbed) {
    return undefined;
  }

  const embedPath = EMBED_PATH_BY_TYPE[trustedEmbed.embedType];

  return `${getMsr2Base()}/${embedPath}/${encodeURIComponent(trustedEmbed.resourceId)}`;
};

/** Embeds that cannot make a registration; any other type needs a Current Member. */
const UNGATED_EMBED_TYPES: ReadonlySet<EmbedType> = new Set<EmbedType>([
  'competing-claim',
  'consolidated-payments',
  'payments',
]);

export const isMembershipGatedEmbed = (embedSrc?: null | string): boolean => {
  const trustedEmbed = parseTrustedEmbedSrc(embedSrc);

  return Boolean(
    trustedEmbed && !UNGATED_EMBED_TYPES.has(trustedEmbed.embedType),
  );
};

/** `add-title` names the workspace MSR2 registers against, so only the route's own may open. */
export const isForeignWorkspaceAddTitle = (
  embedSrc: null | string,
  workspaceId?: string,
): boolean => {
  const trustedEmbed = parseTrustedEmbedSrc(embedSrc);

  return (
    trustedEmbed?.embedType === 'add-title' &&
    trustedEmbed.resourceId !== workspaceId
  );
};

const getCompletionOutcome = (
  value?: string,
): Extract<EmbeddedWorkflowOutcome, 'completed' | 'draft'> | undefined => {
  if (!value) return undefined;
  if (COMPLETED_MESSAGE_VALUES.has(value)) return 'completed';
  if (DRAFT_MESSAGE_VALUES.has(value)) return 'draft';

  return undefined;
};

const hasCloseSignal = (...values: (string | undefined)[]): boolean =>
  values.some((value) => Boolean(value && CLOSE_MESSAGE_VALUES.has(value)));

const normalizeEmbeddedWorkflowMessage = (
  messageData: EmbeddedWorkflowMessageData,
): NormalizedEmbeddedWorkflowMessage => ({
  claimStatus: normalizeMessageValue(messageData.claimStatus),
  leadStatus: normalizeMessageValue(messageData.leadStatus),
  outcome: normalizeMessageValue(messageData.outcome),
  status: normalizeMessageValue(messageData.status),
  type: normalizeMessageValue(messageData.type),
});

const getTerminalMessageOutcome = ({
  outcome,
  type,
}: NormalizedEmbeddedWorkflowMessage): EmbeddedWorkflowOutcome | undefined =>
  getCompletionOutcome(outcome) ??
  (outcome === 'closed' ? 'closed' : undefined) ??
  getCompletionOutcome(type);

export const getEmbeddedWorkflowTitleName = (
  titleName: null | string,
): string => titleName?.trim() || 'Your';

export const getEmbeddedWorkflowOutcome = (
  messageData: EmbeddedWorkflowMessageData,
): EmbeddedWorkflowOutcome | undefined => {
  const message = normalizeEmbeddedWorkflowMessage(messageData);
  const terminalOutcome = getTerminalMessageOutcome(message);

  if (terminalOutcome) return terminalOutcome;

  if (!hasCloseSignal(message.outcome, message.type)) {
    return undefined;
  }

  if (messageData.completed === true) return 'completed';
  if (messageData.completed === false || messageData.isDraft === true) {
    return 'draft';
  }

  const closedWorkflowOutcome =
    getCompletionOutcome(message.claimStatus) ??
    // The embedded competing-claim flow currently sends this for both review-later and no-claims actions.
    (message.leadStatus === 'lead' ? 'draft' : undefined) ??
    getCompletionOutcome(message.status);

  return closedWorkflowOutcome ?? 'closed';
};
